import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Bookmark,
  CalendarIcon,
  ChevronsDown,
  ChevronRight,
  Compass,
  ExternalLink,
  Bell,
  Gift,
  Globe,
  GraduationCap,
  Home,
  ImageIcon as Image,
  LinkIcon,
  Loader,
  Megaphone,
  MessageSquare,
  SendIcon,
  Settings,
  Sparkles,
  Speech,
  SquarePen,
  Users,
  UsersRound,
  Zap,
} from 'lucide-react';
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
  useSidebar,
} from '@artstorefronts/ui';
import { useQuery } from '@apollo/client';
import { toast } from 'sonner';

import { useTranslation } from 'react-i18next';

import { useAuth } from '../context/auth';
import { useAnalytics } from '~/context/analytics';
import {
  NOTIFICATION_COUNTS_QUERY,
  COMMUNITY_POSTS_SINCE_LAST_VISIT_QUERY,
  SOCIAL_BADGE_COUNTS_QUERY,
  MY_COMMUNITIES_QUERY,
  MY_GUILDS_QUERY,
} from '../graphql/queries';
import { buildTownSquareUrl } from '../utils';
import { chatHistory } from '~/utils/chatHistory';
import { getChatNavigationPath, isChatActive } from '~/utils/chatNavigation';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useReferralSettings } from '~/hooks/useReferralSettings';
import { useMyChats } from '~/hooks/useMyChats';
import { CHROME_BOTTOM_CSS } from '~/lib/chromeGeometry';
import { ChatContextMenu } from './ChatContextMenu';
import { GuildMark } from './guild/GuildMark';
import { MY_GIFT_ALLOWANCE } from './InviteFriendModal';
import { SparkUsage } from './SparkUsage';

/**
 * The ArtHelper feature-request board.
 *
 * Deliberately bare. Canny's create form ignores `title`, `details` and `category`, its
 * categories are feature areas rather than sources, and Identify attaches traits to the
 * person rather than the post — so there is no way to tag the source app in this URL.
 * Counting the click is the honest half; see `NavFooter`.
 */
const FEEDBACK_URL = 'https://arthelper.canny.io/feature-requests';

const INTEGRATIONS_ENABLED = import.meta.env.VITE_INTEGRATIONS_ENABLED === 'true';

interface AppSidebarProps {
  onNewChat?: () => void;
}

function NavMain({ onNewChat }: { onNewChat?: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('nav');
  const { amplitude, intercom } = useAnalytics();
  const { hasPermissions, profile, user } = useAuth();
  const { enabled: connectionsEnabled } = useFeatureFlag('connections_and_dms');
  // State for collapsible sections
  const [isPromoteOpen, setIsPromoteOpen] = useState(false);
  const [isExploreOpen, setIsExploreOpen] = useState(false);

  // Auto-open promote menu when on grow-audience pages
  useEffect(() => {
    if (
      location.pathname === '/grow-audience' ||
      location.pathname === '/art-marketing-autopilot' ||
      location.pathname === '/grow-your-audience'
    ) {
      setIsPromoteOpen(true);
    }
  }, [location.pathname]);

  // Fetch notification counts (read from cache, polling handled by dashboardLayout)
  const { data: notificationsData } = useQuery(NOTIFICATION_COUNTS_QUERY, {
    fetchPolicy: 'cache-only',
  });
  const unreadCount = notificationsData?.notifications?.unreadCount || 0;

  // Cache-only — dashboardLayout polls the actual query so the badges stay
  // in sync without every sidebar render kicking off another network request.
  const { data: socialCountsData } = useQuery(SOCIAL_BADGE_COUNTS_QUERY, {
    fetchPolicy: 'cache-only',
    skip: !connectionsEnabled,
  });
  const incomingRequestCount = socialCountsData?.incomingConnectionRequests?.totalCount || 0;
  const unreadDmCount = socialCountsData?.unreadDmCount || 0;

  // Community posts since last visit — read from server-stored timestamp
  const { data: communityPostsData } = useQuery(COMMUNITY_POSTS_SINCE_LAST_VISIT_QUERY, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 60000,
    skip: !user,
  });
  const communityPostsSince = communityPostsData?.newCommunityPostsSinceLastVisit || 0;

  // The portfolio lives on town-square, so this row leaves the SPA — plain <a>
  // via buildTownSquareUrl to keep the viewer's locale prefix. Slug-less owners
  // fall through to town-square `/profile`, which still runs ProfileUrlSetup.
  const profileSlug = profile?.profile?.slug;
  const portfolioUrl = buildTownSquareUrl(profileSlug ? `/${profileSlug}` : '/profile');

  const handlePostClick = () => {
    amplitude.track('new_post_clicked', { location: 'sidebar' });
    intercom.trackEvent('new_post_clicked', { location: 'sidebar' });
    navigate('/post', { state: { from: window.location.pathname } });
    toast.dismiss();
  };

  return (
    <>
      <SidebarGroup>
        <SidebarMenu>
          {/* Community (external link) */}
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip={t('home')}
              onClick={() => {
                amplitude.track('community_forum_nav_link_clicked');
                intercom.trackEvent('community_forum_nav_link_clicked');
                toast.dismiss();
              }}
            >
              <a href={`${__TOWN_SQUARE_URL__ || 'https://www.arthelper.com'}/community`} rel="noopener noreferrer">
                <Home className="size-4" />
                <span>{t('home')}</span>
                <span className="bg-red-500 text-white text-[10px] py-1 px-2 leading-none rounded-full">
                  {t('new')}
                </span>
              </a>
            </SidebarMenuButton>
            {communityPostsSince > 0 && (
              <SidebarMenuBadge className="bg-gray-200 text-gray-700">{communityPostsSince}</SidebarMenuBadge>
            )}
          </SidebarMenuItem>

          {/* My Artwork */}
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip={t('my-artwork')}
              isActive={location.pathname === '/my-artwork'}
              onClick={() => {
                amplitude.track('my_artwork_nav_link_clicked');
                intercom.trackEvent('my_artwork_nav_link_clicked');
                toast.dismiss();
              }}
            >
              <NavLink to="/my-artwork">
                <Image className="size-4" />
                <span>{t('my-artwork')}</span>
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>

          {/* My Portfolio & Profile */}
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip={t('my-portfolio-profile')}
              onClick={() => {
                amplitude.track('profile_nav_link_clicked');
                intercom.trackEvent('profile_nav_link_clicked');
                // This document is replaced, so the queued batch would die with it.
                amplitude.flushOnExit();
                toast.dismiss();
              }}
            >
              <a href={portfolioUrl}>
                <Globe className="size-4" />
                <span>{t('my-portfolio-profile')}</span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>

          {/* Notifications */}
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip={t('notifications')}
              isActive={location.pathname === '/notifications'}
              onClick={() => {
                amplitude.track('notifications_nav_link_clicked');
                intercom.trackEvent('notifications_nav_link_clicked');
                toast.dismiss();
              }}
            >
              <NavLink
                to="/notifications"
                onClick={(e) => {
                  // Clicking the tab while already on it is a React Router no-op, so the
                  // page never refetches. Push a fresh state stamp the page reacts to.
                  if (location.pathname === '/notifications') {
                    e.preventDefault();
                    navigate('/notifications', { replace: true, state: { refreshedAt: Date.now() } });
                  }
                }}
              >
                <Bell className="size-4" />
                <span>{t('notifications')}</span>
              </NavLink>
            </SidebarMenuButton>
            {unreadCount > 0 && (
              <SidebarMenuBadge className="bg-red-500 !text-white">
                {unreadCount > 99 ? '99+' : unreadCount}
              </SidebarMenuBadge>
            )}
          </SidebarMenuItem>

          {/* Network (feature-flagged) */}
          {connectionsEnabled && (
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                tooltip={t('my-network')}
                isActive={location.pathname.startsWith('/network')}
                onClick={() => {
                  amplitude.track('network_nav_link_clicked');
                  intercom.trackEvent('network_nav_link_clicked');
                  toast.dismiss();
                }}
              >
                <NavLink to="/network">
                  <UsersRound className="size-4" />
                  <span>{t('my-network')}</span>
                </NavLink>
              </SidebarMenuButton>
              {incomingRequestCount > 0 && (
                <SidebarMenuBadge className="bg-red-500 !text-white">
                  {incomingRequestCount > 99 ? '99+' : incomingRequestCount}
                </SidebarMenuBadge>
              )}
            </SidebarMenuItem>
          )}

          {/* Messages (feature-flagged) — navigates to the full-page
              `/messages` route. The dock stays reachable via its other
              entry points. */}
          {connectionsEnabled && (
            <SidebarMenuItem>
              <SidebarMenuButton
                tooltip={t('messages')}
                isActive={location.pathname.startsWith('/messages')}
                onClick={() => {
                  amplitude.track('messages_nav_link_clicked');
                  intercom.trackEvent('messages_nav_link_clicked');
                  toast.dismiss();
                  navigate('/messages', { replace: location.pathname === '/messages' });
                }}
              >
                <MessageSquare className="size-4" />
                <span>{t('messages')}</span>
              </SidebarMenuButton>
              {unreadDmCount > 0 && (
                <SidebarMenuBadge className="bg-red-500 !text-white">
                  {unreadDmCount > 99 ? '99+' : unreadDmCount}
                </SidebarMenuBadge>
              )}
            </SidebarMenuItem>
          )}

          {/* Saved Content */}
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip={t('saved-content')}
              isActive={location.pathname === '/saved'}
              onClick={() => {
                amplitude.track('saved_content_nav_link_clicked');
                intercom.trackEvent('saved_content_nav_link_clicked');
                toast.dismiss();
              }}
            >
              <NavLink to="/saved">
                <Bookmark className="size-4" />
                <span>{t('saved-content')}</span>
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>

          {/* Promote - Collapsible */}
          <Collapsible asChild open={isPromoteOpen} onOpenChange={setIsPromoteOpen}>
            <SidebarMenuItem>
              <SidebarMenuButton
                tooltip={t('promote')}
                isActive={
                  location.pathname === '/grow-audience' ||
                  location.pathname === '/art-marketing-autopilot' ||
                  location.pathname === '/grow-your-audience'
                }
                onClick={() => setIsPromoteOpen((prev) => !prev)}
              >
                <Megaphone className="size-4" />
                <span>{t('promote')}</span>
              </SidebarMenuButton>
              <CollapsibleTrigger asChild>
                <SidebarMenuAction className="data-[state=open]:rotate-90">
                  <ChevronRight />
                  <span className="sr-only">{t('toggle')}</span>
                </SidebarMenuAction>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <SidebarMenuSub>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton asChild onClick={handlePostClick}>
                      <button type="button">
                        <SendIcon className="size-4" />
                        {t('post-to-social')}
                      </button>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton
                      asChild
                      isActive={location.pathname === '/grow-audience'}
                      onClick={() => {
                        amplitude.track('clicked_grow_your_audience_link');
                        intercom.trackEvent('clicked_grow_your_audience_link');
                        toast.dismiss();
                      }}
                    >
                      <NavLink to="/grow-audience">
                        <Users className="size-4" />
                        {t('get-more-followers')}
                      </NavLink>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton
                      asChild
                      isActive={location.pathname === '/art-marketing-autopilot'}
                      onClick={() => {
                        amplitude.track('clicked_art_marketing_autopilot_link');
                        intercom.trackEvent('clicked_art_marketing_autopilot_link');
                        toast.dismiss();
                      }}
                    >
                      {profile?.isOldAsfMember ? (
                        <a href="https://www.artmarketingcopilot.com/" target="_blank" rel="noopener noreferrer">
                          <Zap className="size-4 shrink-0" />
                          <span className="truncate">{t('automate-marketing')}</span>
                        </a>
                      ) : (
                        <NavLink to="/art-marketing-autopilot">
                          <Zap className="size-4 shrink-0" />
                          <span className="truncate">{t('automate-marketing')}</span>
                        </NavLink>
                      )}
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  {/* Calendar (conditional) — nested under Promote */}
                  {INTEGRATIONS_ENABLED && (
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        asChild
                        isActive={location.pathname === '/calendar'}
                        onClick={() => {
                          amplitude.track('calendar_nav_link_clicked');
                          intercom.trackEvent('calendar_nav_link_clicked');
                          toast.dismiss();
                        }}
                      >
                        <NavLink to="/calendar">
                          <CalendarIcon className="size-4" />
                          {t('calendar')}
                        </NavLink>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  )}
                </SidebarMenuSub>
              </CollapsibleContent>
            </SidebarMenuItem>
          </Collapsible>

          {/* Integrations (conditional) */}
          {INTEGRATIONS_ENABLED && (
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                tooltip={t('integrations')}
                isActive={location.pathname === '/integrations'}
                onClick={() => {
                  amplitude.track('integrations_nav_link_clicked');
                  intercom.trackEvent('integrations_nav_link_clicked');
                  toast.dismiss();
                }}
              >
                <NavLink to="/integrations">
                  <LinkIcon className="size-4" />
                  <span>{t('integrations')}</span>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}

          {/* Admin (conditional) */}
          {hasPermissions(['role:admin']) && (
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                tooltip={t('admin')}
                isActive={location.pathname.startsWith('/admin')}
                onClick={() => {
                  amplitude.track('admin_nav_link_clicked');
                  intercom.trackEvent('admin_nav_link_clicked');
                  toast.dismiss();
                }}
              >
                <NavLink to="/admin/home">
                  <Settings className="size-4" />
                  <span>{t('admin')}</span>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}

          {/* Explore - Mobile only (these links are hidden in the site header on mobile) */}
          <Collapsible asChild open={isExploreOpen} onOpenChange={setIsExploreOpen} className="md:hidden">
            <SidebarMenuItem>
              <SidebarMenuButton
                tooltip={t('explore')}
                onClick={() => {
                  window.location.href = `${__TOWN_SQUARE_URL__ || 'https://www.arthelper.com'}/about`;
                }}
              >
                <Compass className="size-4" />
                <span>{t('explore')}</span>
              </SidebarMenuButton>
              <CollapsibleTrigger asChild>
                <SidebarMenuAction className="data-[state=open]:rotate-90 md:hidden">
                  <ChevronRight />
                  <span className="sr-only">{t('toggle')}</span>
                </SidebarMenuAction>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <SidebarMenuSub>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton asChild>
                      <a href={`${__TOWN_SQUARE_URL__ || 'https://www.arthelper.com'}/art`}>{t('art')}</a>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton asChild>
                      <a href={`${__TOWN_SQUARE_URL__ || 'https://www.arthelper.com'}/artists`}>{t('artists')}</a>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton asChild>
                      <a href={`${__TOWN_SQUARE_URL__ || 'https://www.arthelper.com'}/about`}>
                        {t('what-is-arthelper')}
                      </a>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                </SidebarMenuSub>
              </CollapsibleContent>
            </SidebarMenuItem>
          </Collapsible>
        </SidebarMenu>
      </SidebarGroup>

      {/* Keyed by viewer so an account switch remounts it and never shows the prior list. */}
      <NavGuilds key={user?.user_id ?? 'signed-out'} />

      <NavCommunities />

      {/* Assistant/Chat Section (conditional) */}
      {__CHAT_ENABLED__ && <NavChat onNewChat={onNewChat} />}
    </>
  );
}

interface SidebarCommunity {
  id: string;
  name: string;
  slug: string;
  avatarUrl?: string | null;
  hasUnseenPosts?: boolean;
}

function CommunityRowAvatar({
  name,
  avatarUrl,
  hasUnseenPosts,
}: {
  name: string;
  avatarUrl?: string | null;
  hasUnseenPosts?: boolean;
}) {
  return (
    <div className="relative size-4 shrink-0">
      {avatarUrl ? (
        <div className="relative size-4 rounded-full overflow-hidden bg-gray-100">
          <img src={avatarUrl} alt="" className="absolute inset-0 size-full object-cover" />
        </div>
      ) : (
        <div className="size-4 rounded-full bg-gray-200 flex items-center justify-center">
          <span className="text-[8px] font-bold text-gray-600 leading-none">{name[0]?.toUpperCase()}</span>
        </div>
      )}
      {hasUnseenPosts && (
        // Same absolutely-positioned dot as the Town Square sidebar — display
        // only here (this SPA never writes the visit; it clears once Town
        // Square records it and the next poll refetches this row).
        <span
          className="absolute -left-0.5 -top-0.5 size-1.5 rounded-full bg-red-500 ring-2 ring-sidebar"
          aria-hidden="true"
        />
      )}
    </div>
  );
}

function NavCommunities() {
  const { t } = useTranslation('nav');
  const { t: tc } = useTranslation('community');
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(true);

  // Community pages live in town-square, so rows link out to that origin
  // (mirrors the existing Community/Explore external links in this sidebar).
  const townSquareUrl = __TOWN_SQUARE_URL__ || 'https://www.arthelper.com';

  const userId = user?.user_id ?? null;

  // `myCommunities` is ordered by activity server-side (lastActivityAt DESC),
  // so the list renders most-active-first with no client sort.
  const { data } = useQuery(MY_COMMUNITIES_QUERY, {
    fetchPolicy: 'cache-only',
    skip: !user,
  });
  const communities: SidebarCommunity[] = data?.myCommunities ?? [];

  // cache-and-network can return the prior viewer's bundle until refetch settles.
  // Compare the query's `user.asfId` to the live auth id instead of trusting
  // NetworkStatus.ready (cached hits are also "ready" before the network round-trip).
  const viewerAsfId = data?.user?.asfId?.toString();
  const communitiesTrusted = !!userId && viewerAsfId === String(userId);

  // Gate on the live `user` so a signed-out viewer never sees a stale
  // cache-and-network community list; wait for viewer-scoped data before showing
  // either the populated list or the empty-state CTA.
  if (!user) return null;
  if (!communitiesTrusted) return null;

  if (communities.length === 0) {
    return (
      <SidebarGroup className="group-data-[collapsible=icon]:hidden">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip={tc('discover')}>
              <a href={`${townSquareUrl}/community/all`}>
                <span>{tc('discover')}</span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroup>
    );
  }

  return (
    <SidebarGroup className="group-data-[collapsible=icon]:hidden">
      <SidebarMenu>
        <Collapsible asChild open={isOpen} onOpenChange={setIsOpen}>
          <SidebarMenuItem>
            <SidebarMenuButton tooltip={t('my-communities')} onClick={() => setIsOpen((prev) => !prev)}>
              <span>{t('my-communities')}</span>
            </SidebarMenuButton>
            <CollapsibleTrigger asChild>
              <SidebarMenuAction className="data-[state=open]:rotate-90">
                <ChevronRight />
                <span className="sr-only">{t('toggle')}</span>
              </SidebarMenuAction>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarMenuSub>
                {communities.map((community) => (
                  <SidebarMenuSubItem key={community.id}>
                    <SidebarMenuSubButton asChild>
                      <a href={`${townSquareUrl}/community/${community.slug}`}>
                        <CommunityRowAvatar
                          name={community.name}
                          avatarUrl={community.avatarUrl}
                          hasUnseenPosts={community.hasUnseenPosts}
                        />
                        <span className="truncate">{community.name}</span>
                      </a>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                ))}
              </SidebarMenuSub>
            </CollapsibleContent>
          </SidebarMenuItem>
        </Collapsible>
      </SidebarMenu>
    </SidebarGroup>
  );
}

interface SidebarGuild {
  id: string;
  name: string;
  slug: string;
  /** Unread messages in the guild's room — chat only, the same count town-square shows. */
  viewerUnreadChat?: number | null;
}

/**
 * My Guilds — the client's copy of town-square's `NavGuilds`, one group above My
 * Communities, as there. Guild pages live in town-square, so rows link out to that origin.
 *
 * A private guild is listed by name here because this is the member's OWN sidebar. The
 * rule that hides a private guild's name applies to someone else's profile.
 *
 * `network-only`, not the cache-only read NavCommunities uses: `Query.user` is shared with
 * other queries but `Query.myGuilds` is written only here, so after an account switch a
 * sibling query can refresh `user` while `myGuilds` still holds the prior member's private
 * guilds. Fetching per mount (remounted per viewer by its `key`) keeps the two paired.
 */
function NavGuilds() {
  const { t } = useTranslation('nav');
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(true);
  const townSquareUrl = __TOWN_SQUARE_URL__ || 'https://www.arthelper.com';

  const { data } = useQuery(MY_GUILDS_QUERY, {
    fetchPolicy: 'network-only',
    pollInterval: 60000,
    skip: !user,
  });
  const guilds: SidebarGuild[] = data?.myGuilds ?? [];

  // Belt and braces: the response's viewer must still be the signed-in one.
  const userId = user?.user_id ?? null;
  const guildsTrusted = !!userId && data?.user?.asfId?.toString() === String(userId);

  if (!guildsTrusted) return null;
  if (guilds.length === 0) return null;

  return (
    <SidebarGroup className="group-data-[collapsible=icon]:hidden">
      <SidebarMenu>
        <Collapsible asChild open={isOpen} onOpenChange={setIsOpen}>
          <SidebarMenuItem>
            <SidebarMenuButton tooltip={t('my-guilds')} onClick={() => setIsOpen((prev) => !prev)}>
              <GuildMark size={16} />
              <span>{t('my-guilds')}</span>
            </SidebarMenuButton>
            <CollapsibleTrigger asChild>
              <SidebarMenuAction className="data-[state=open]:rotate-90">
                <ChevronRight />
                <span className="sr-only">{t('toggle')}</span>
              </SidebarMenuAction>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarMenuSub>
                {guilds.map((guild) => (
                  <SidebarMenuSubItem key={guild.id}>
                    <SidebarMenuSubButton asChild>
                      <a href={`${townSquareUrl}/guilds/${guild.slug}`}>
                        <span className="truncate">{guild.name}</span>
                        {guild.viewerUnreadChat ? (
                          <span
                            className="ml-auto shrink-0 rounded-full bg-red-500 px-1.5 text-[11px] font-semibold leading-[18px] text-white"
                            aria-label={t('guild-unread', { count: guild.viewerUnreadChat })}
                          >
                            {guild.viewerUnreadChat > 99 ? '99+' : guild.viewerUnreadChat}
                          </span>
                        ) : null}
                      </a>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                ))}
              </SidebarMenuSub>
            </CollapsibleContent>
          </SidebarMenuItem>
        </Collapsible>
      </SidebarMenu>
    </SidebarGroup>
  );
}

function NavChat({ onNewChat }: { onNewChat?: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('nav');
  const { t: tc } = useTranslation('common');
  const { amplitude, intercom } = useAnalytics();

  // Fetch chats for the sidebar, one page at a time
  const { chats, hasNextPage, loadMore, loadingMore } = useMyChats();

  const handleChatClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/chat');
    amplitude.track('chat_nav_link_clicked');
    intercom.trackEvent('chat_nav_link_clicked');
    toast.dismiss();
  };

  return (
    <SidebarGroup className="group-data-[collapsible=icon]:hidden">
      <SidebarGroupLabel>{t('assistant')}</SidebarGroupLabel>
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton asChild tooltip={t('assistant')} onClick={handleChatClick}>
            <NavLink to="/chat">
              <Sparkles className="size-4" />
              <span>{t('assistant')}</span>
            </NavLink>
          </SidebarMenuButton>
          <SidebarMenuAction asChild>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (onNewChat) {
                  onNewChat();
                } else {
                  chatHistory.clearLastConversation();
                  navigate('/chat', { replace: true });
                }
                amplitude.track('chat_nav_link_clicked');
                intercom.trackEvent('chat_nav_link_clicked');
                toast.dismiss();
              }}
              className="h-6 px-2 !aspect-auto !w-auto"
            >
              <SquarePen className="size-3" />
              <span>{t('new-chat')}</span>
            </Button>
          </SidebarMenuAction>
        </SidebarMenuItem>

        {/* Chat History List */}
        {chats.map((chat: { id: string; title?: string; shared?: boolean }) => (
          <SidebarMenuItem key={chat.id}>
            <SidebarMenuButton
              asChild
              tooltip={chat.title || t('untitled-chat')}
              isActive={isChatActive(chat, location)}
              onClick={(e) => {
                // Don't trigger if clicking on the context menu
                if ((e.target as HTMLElement).closest('[data-no-drag-select="true"]')) {
                  return;
                }
                const targetPath = getChatNavigationPath(chat);
                navigate(targetPath, { state: { from: location.pathname + location.search } });
                amplitude.track('chat_nav_link_clicked');
                intercom.trackEvent('chat_nav_link_clicked');
                toast.dismiss();
              }}
            >
              <button type="button" className="flex items-center gap-2 min-w-0 flex-1">
                <span className="truncate">{chat.title || t('untitled-chat')}</span>
                {chat.shared && (
                  <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" title={t('shared-chat')} />
                )}
              </button>
            </SidebarMenuButton>
            <SidebarMenuAction asChild>
              <ChatContextMenu
                chatId={chat.id}
                chatTitle={chat.title || t('untitled-chat')}
                chatType={(chat as any).type}
                chatTargetId={(chat as any).targetId}
                chatShared={chat.shared}
                className="bg-white hover:bg-gray-100"
              />
            </SidebarMenuAction>
          </SidebarMenuItem>
        ))}

        {hasNextPage && (
          <SidebarMenuItem>
            <SidebarMenuButton asChild onClick={loadMore}>
              <button type="button" disabled={loadingMore} className="flex items-center gap-2 min-w-0 flex-1">
                {loadingMore ? <Loader className="size-4 animate-spin" /> : <ChevronsDown className="size-4" />}
                <span className="truncate">{tc('load-more')}</span>
              </button>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}
      </SidebarMenu>
    </SidebarGroup>
  );
}

/**
 * "Refer a friend" — the client's copy of town-square's `ReferFriendNavItem`, same label,
 * same emerald gift box, same slot above Tutorials, same red "N left" badge when the live
 * mechanism gifts a Human-Made subscription, so the two rails agree. The referrals
 * dashboard lives in town-square, so this is a cross-app <a> through `buildTownSquareUrl`
 * (keeps the locale prefix town-square's <Link> would add); `?invite=1` opens it with the
 * invite modal already popped.
 *
 * Gated on `reputation_points_ux` like town-square's entry and /referrals itself — off, the
 * page bounces home, so the row hides rather than dangling. The label is the shared
 * `nav.refer-a-friend` key, so both rails read the same in every locale.
 */
function ReferFriendNavItem() {
  const { t } = useTranslation('nav');
  const { enabled } = useFeatureFlag('reputation_points_ux');
  const referral = useReferralSettings();
  // Only a subscription-gifting mechanism has an allowance to advertise; points-only shows
  // just the colored icon.
  const { data } = useQuery<{ myGiftSubscriptionAllowance: { remaining: number } }>(MY_GIFT_ALLOWANCE, {
    skip: !enabled || !referral.grantsSubscription,
    fetchPolicy: 'cache-and-network',
  });

  if (!enabled) {
    return null;
  }

  const remaining = referral.grantsSubscription ? (data?.myGiftSubscriptionAllowance?.remaining ?? 0) : 0;

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild tooltip={t('refer-a-friend')}>
        <a href={buildTownSquareUrl('/referrals?invite=1')}>
          <Gift className="size-4 text-emerald-600" />
          <span>{t('refer-a-friend')}</span>
        </a>
      </SidebarMenuButton>
      {remaining > 0 && (
        <SidebarMenuBadge className="bg-transparent font-semibold text-red-600">
          {t('refer-a-friend-left', { count: remaining })}
        </SidebarMenuBadge>
      )}
    </SidebarMenuItem>
  );
}

/**
 * The pinned foot of the rail: the spark meter, "Refer a friend", then the utility block
 * held at the very bottom — Tutorials directly above Feedback.
 *
 * `SidebarFooter` is a SIBLING of `SidebarContent`, never a child of it, so everything here
 * stays put while the nav above scrolls — the ui primitive already gives `SidebarContent`
 * its own `min-h-0 flex-1 overflow-auto`. A row held down by `mt-auto` INSIDE that pane
 * looks identical until the list is long enough to scroll, and then the row a confused
 * artist is reaching for scrolls away with everything else. Do not move this block up.
 * -> `agent-standards/docs/in-app-help-placement-pattern.md` §1
 *
 * Below 768px the ui `Sidebar` renders a Sheet with the SAME children, so the phone drawer
 * carries this block already — there is no second call site to keep in sync.
 */
function NavFooter() {
  const { t } = useTranslation('nav');
  const { amplitude, intercom } = useAnalytics();
  const { isMobile } = useSidebar();
  // Destructured rather than held as `location`, which the Feedback handler below already
  // uses for the surface name — a second `location` in this scope would shadow it.
  const { pathname } = useLocation();

  return (
    <SidebarFooter>
      <SidebarSeparator />

      {/* `empty:hidden` because `SparkUsage` renders nothing on an unlimited plan, and the
          padding would otherwise hold a 16px gap open below it. The wrapper keeps the
          padding rather than moving it into the component: `SparkSelectorGrid` renders the
          same component in a box of its own. */}
      <div className="px-4 py-2 empty:hidden group-data-[collapsible=icon]:hidden">
        <SparkUsage mode="dark" />
      </div>

      {/* The utility block: the two doors somebody reaches for when the product is not
          doing what they expected. Tutorials answers "how is this meant to work"; Feedback
          answers "it does not do what I need".

          TUTORIALS SITS DIRECTLY ABOVE FEEDBACK, and the order is the point rather than a
          preference: somebody who has just watched a walkthrough and STILL has a gap is the
          person with the most useful thing to say. Same two rows, same order, in every app
          in the fleet, so learning where this lives in one product learns it in all of them
          (`agent-standards/docs/in-app-help-placement-pattern.md` §1).

          The block sits BELOW the spark meter, which is where Feedback was moved to when it
          was pinned to the foot of both sidebars. Tutorials joins it there rather than
          reinstating an earlier arrangement above the meter — the standard fixes the two
          rows' order relative to each other, not their height on the rail. */}
      <SidebarMenu>
        <ReferFriendNavItem />

        {/* Internal, so a NavLink with `isActive` like every other row of ours — unlike
            Feedback below, which leaves for another origin. */}
        <SidebarMenuItem>
          <SidebarMenuButton
            asChild
            tooltip={t('tutorials')}
            isActive={pathname === '/tutorials'}
            onClick={() => {
              const location = isMobile ? 'sidebar_mobile' : 'sidebar';
              amplitude.track('tutorials_nav_link_clicked', { location });
              intercom.trackEvent('tutorials_nav_link_clicked', { location });
            }}
          >
            <NavLink to="/tutorials">
              <GraduationCap className="size-4" />
              <span>{t('tutorials')}</span>
            </NavLink>
          </SidebarMenuButton>
        </SidebarMenuItem>

        {/* Feedback — the door for somebody the product has already confused, which is why
            it is labelled and sits on the rail instead of three clicks into the avatar menu.
            A plain <a target="_blank">, never a NavLink: it leaves for Canny, so it is not a
            page of ours and must never be `aria-current`. No `flushOnExit()` either — a new
            tab leaves this document alive, unlike the rows that replace it. */}
        <SidebarMenuItem>
          <SidebarMenuButton
            asChild
            tooltip={t('feedback')}
            onClick={() => {
              // The only attribution Canny allows us: the click. See FEEDBACK_URL.
              const location = isMobile ? 'sidebar_mobile' : 'sidebar';
              amplitude.track('feedback_nav_link_clicked', { location });
              intercom.trackEvent('feedback_nav_link_clicked', { location });
            }}
          >
            <a href={FEEDBACK_URL} target="_blank" rel="noopener noreferrer">
              <Speech className="size-4" />
              <span>{t('feedback')}</span>
              <ExternalLink
                aria-hidden="true"
                className="ml-auto !size-3.5 opacity-60 group-data-[collapsible=icon]:hidden"
              />
            </a>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarFooter>
  );
}

export function AppSidebar({ onNewChat }: AppSidebarProps) {
  return (
    <Sidebar
      collapsible="icon"
      variant="floating"
      style={
        {
          // Lands on the sidebar's own `fixed inset-y-0 h-svh` panel, so it has to clear the
          // whole chrome — suite bar plus header — not just the header, or the panel's top
          // 56px hides behind the bar and the first item is clipped. Not a constant 112px
          // either: the bar leaves layout when the artist collapses it, and this tracks that
          // through the variable dashboardLayout publishes. The fallback in
          // `CHROME_BOTTOM_CSS` is the pre-suite-bar offset, so a chrome-free mount is
          // unchanged. Ignored entirely on mobile, where the ui `Sidebar` renders a Sheet
          // and drops this style.
          top: CHROME_BOTTOM_CSS,
          height: `calc(100svh - ${CHROME_BOTTOM_CSS})`,
          '--sidebar-width': '18rem',
        } as React.CSSProperties
      }
    >
      <SidebarContent>
        <NavMain onNewChat={onNewChat} />
      </SidebarContent>

      <NavFooter />
    </Sidebar>
  );
}
